import React, { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);

  const addItem = useCallback((product, variation, quantity, modifiers, notes, modifierCost = 0) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(item =>
        item.product_id === product.id &&
        item.variation_id === (variation?.id || null) &&
        JSON.stringify(item.modifiers) === JSON.stringify(modifiers) &&
        (item.notes || '') === (notes || '')
      );
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], quantity: updated[existingIndex].quantity + quantity };
        return updated;
      }
      const basePrice = Number(variation?.price || product.price);
      return [...prev, {
        id: Date.now(),
        product_id: product.id,
        product_name: product.name,
        variation_id: variation?.id || null,
        variation_name: variation?.name || null,
        price: basePrice + modifierCost,
        quantity,
        modifiers: modifiers || {},
        notes: notes || ''
      }];
    });
  }, []);

  const updateQuantity = useCallback((itemId, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(item => item.id !== itemId));
    } else {
      setItems(prev => prev.map(item => item.id === itemId ? { ...item, quantity } : item));
    }
  }, []);

  const removeItem = useCallback((itemId) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const updateItem = useCallback((itemId, updates) => {
    setItems(prev => prev.map(item => item.id === itemId ? { ...item, ...updates } : item));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateItem, updateQuantity, removeItem, clearCart, total, itemCount }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
